import axios from "axios";
import moment from "moment-timezone";
import Modal from "react-modal";
import { DatePicker } from "rsuite";
import Col from "react-bootstrap/Col";
import ReactPaginate from "react-paginate";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import WeekTotalDisplay from "./helpers/WeekTotalDisplay.jsx";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  useClientProjectDetails,
  useGroupedTimerData,
  useTagDetails,
  useTimerData,
  useTimeZone,
} from "./TimerDataApi.jsx";
import { setTimerData } from "../../../../features/timerData/timerDataSlice.js";
import TimerCard from "./trackerModules/Card.jsx";
import {
  debounce,
  formatTime,
  getMomentDate,
  findLeastDate,
  findExtremeDate,
  validateTimerStartData,
  validateTimerStopData,
  buildTimerStopPostData,
  getCurrentTime,
} from "./services/trackerServices.js";
import TimeZoneModal from "../../../Modal/SetDefaultModal.jsx";
import { useFetchUsersTimer } from "../../TimeTrackerTeam/TimeTrackerTeamMembers/teamMemberApi.jsx";
import { setAppLoading } from "../../../../features/appSettings/appSettingsSlice.js";
import { reset as eventDetailReset } from '../../../../features/eventDetails/eventDetailsSlice.js';
import TimerList from "./trackerModules/TimerList.jsx";
import {
  TaskInput,
  TagDropdown,
  BillableIcon,
  TimeInputWithPicker,
  DurationInput,
  ClockIcon,
  BulkImportModal,
  SkippedRowsModal,
  LoadingModal,
  StatusDropdown,
} from "./trackerComponents";
import { ProjectSelector } from "./TimerForm";
import { LiveTimer } from "./TimerControls";
import { handleError, handleApiError, showErrorToast } from "./utils/errorHandling";
import {
  stopCurrentTimer,
  startNewTimer,
  syncTimerStateToStorage,
  clearTimerState,
  validateTimerResumeData,
} from "./utils/timerResumeService";
import {
  secureGetItem,
  STORAGE_KEYS,
} from "./utils/secureStorage";
// Input sanitization is now handled in timerStartHelpers
import {
  filterAndFindExtremes,
  findExtremesOfLastWeek,
  findExtremesByWeek,
} from "./helpers/dateCalculations";
import { DatePickerIconSVG } from "./trackerComponents/SvgIcons";
import {
  handleBulkImportUpload,
  handleBulkImportFileSelect,
} from "./utils/bulkImportHandlers";
import {
  getLockHours,
  handleDateIconClick as handleDateIconClickHelper,
  handlePageChange as handlePageChangeHelper,
  isAdminUser,
} from "./utils/trackerHelpers";
import { filterUsers, createUserHelpers } from "./utils/userHelpers";
import { createManualTimeShowEditHandlers } from "./utils/manualTimeShowEditHelpers";
import { filterTagItems, filterClientData } from "./utils/dataFilterHelpers";
import { createClickOutsideDropdownHandler } from "./utils/clickOutsideHelpers";
import { createCustomTimeSubmissionHandler } from "./utils/customTimeSubmissionHelpers";
import { createDataInitializationHandler } from "./utils/dataInitializationHelpers";
import { createTimerModeHandlers } from "./utils/timerModeHelpers";
import { createBulkImportModalHelpers } from "./utils/bulkImportModalHelpers";
import { createTimeValidationHandler } from "./utils/timeValidationHelpers";
import { createTimeInputValidationHandler } from "./utils/timeInputValidationHelpers";
import { createFilteredDataInitializationHandler } from "./utils/filteredDataHelpers";
import {
  fadeIn,
  pointerCursorStyle,
  DEFAULT_START_TIME_12HR,
  DEFAULT_END_TIME_12HR,
  DEFAULT_START_TIME_24HR,
  DEFAULT_END_TIME_24HR,
  DEFAULT_DURATION,
  toastConfig,
  toastConfigWithTheme,
  getErrorBorderStyle,
  disabledButtonStyle,
  taskInputClassName,
  taskInputErrorClassName,
  textOverflowStyle,
} from "./utils/constants";
import { useTimePickerInitialization } from "./hooks/useTimePickerInitialization";
import { autoLoadTimerData } from "./utils/timerHelpers";
import { createManualTimeHandlers } from "./utils/manualTimeHandlers";
import { createProjectDropdownHelpers } from "./utils/projectDropdownHelpers";
import { createTimerReduxActions } from "./utils/reduxActions";
import {
  createToggleHandler,
  createStatusChangeHandler,
  createInputChangeHandler,
} from "./utils/simpleHandlers";
import { persistTimerStartData } from "./utils/timerStartHelpers";
import { getRunningTaskFromStorage } from "./utils/timerStorageHelpers";
import { startTimerApiCall } from "./utils/timerStartService";
import { useTrackerRedux } from "./hooks/useTrackerRedux";

const PlusCircleIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="20" fill="#6479F8" />
    <path d="M20.7144 12.381V27.1429" stroke="white" strokeWidth="1.5" />
    <path d="M28.0952 19.7619L13.3333 19.7619" stroke="white" strokeWidth="1.5" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.52625 15.8539C6.55157 15.8771 6.57923 15.8979 6.60884 15.9161L6.65014 15.9361L6.70235 15.9621L6.75689 15.9782L6.80482 15.9904C6.87651 16.0043 6.95092 16.0043 7.023 15.9904L7.07092 15.9782L7.12547 15.9621L7.17767 15.9361L7.21897 15.9161C7.24897 15.8979 7.27664 15.8771 7.30196 15.8539L12.6977 10.9C12.8773 10.7014 12.8625 10.41 12.6634 10.2275C12.4643 10.045 12.1464 10.0314 11.9298 10.1961L7.45702 14.3V1.5C7.45702 1.22393 7.21274 1 6.91157 1C6.6104 1 6.36612 1.22393 6.36612 1.5V14.3L1.89339 10.1961C1.67677 10.0314 1.35884 10.045 1.15975 10.2275C0.960661 10.41 0.945856 10.7014 1.12547 10.9L6.52547 15.8539H6.52625Z" fill="white" stroke="white" strokeWidth="0.5" />
  </svg>
);

const Tracker = (props) => {
  // ==================== Redux & Selectors ====================
  const {
    dispatch,
    appSettings,
    timerRedux,
    token,
    user,
    timerData,
    timeTrackerLog,
    eventDetails,
  } = useTrackerRedux();

  // ==================== Timer Core State ====================
  const [taskName, setTaskName] = useState("");
  const [taskError, setTaskError] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [projectId, setProjectId] = useState("");
  const [sprintName, setSprintName] = useState("");
  const [sprintId, setSprintId] = useState("");
  const [client, setClient] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [isBillable, setIsBillable] = useState(null);
  const [tag, setTag] = useState(null);
  const [tagId, setTagId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [trackTask, setTrackTask] = useState(null);
  const [trigger, setTrigger] = useState(false);
  const [stopRunning, setStopRunning] = useState(null);
  const [timerUpdate, setTimerUpdate] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // ==================== Manual Time Entry State ====================
  const [customeTime, setCustomeTime] = useState(false);
  const [date, setDate] = useState(() => moment().tz(global.config.getTimeZone()).toDate());
  const [startTimeManual, setStartTimeManual] = useState(DEFAULT_START_TIME_12HR);
  const [startTimeTemp, setStartTimeTemp] = useState(DEFAULT_START_TIME_12HR);
  const [startTimeManual24, setStartTimeManual24] = useState(DEFAULT_START_TIME_24HR);
  const [endTimeManual, setEndTimeManual] = useState(DEFAULT_END_TIME_12HR);
  const [endTimeTemp, setEndTimeTemp] = useState(DEFAULT_END_TIME_12HR);
  const [endTimeManual24, setEndTimeManual24] = useState(DEFAULT_END_TIME_24HR);
  const [uFormatedTimeManual, setUFormatedTimeManual] = useState(DEFAULT_DURATION);
  const [uDuration, setUDuration] = useState(null);
  const [uManualStartTimeError, setUManualStartTimeError] = useState(false);
  const [uManualEndTimeError, setUManualEndTimeError] = useState(false);
  const [uManualFormatedTimeError, setUManualFormatedTimeError] = useState(false);
  const [isTimeInputActive, setIsTimeInputActive] = useState(false);

  // ==================== Global Edit State ====================
  const [editedId, setEditedId] = useState(null);
  const [splitted, setSplitted] = useState(false);
  const [splittedDate, setSplittedDate] = useState([]);

  // ==================== Dropdown & Modal State ====================
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [loadingModalIsOpen, setLoadingModalIsOpen] = useState(false);
  const [taskModalIsOpen, setTaskModalIsOpen] = useState(false);
  const [showTimeZoneModal, setShowTimeZoneModal] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [showSkippedRowsModal, setShowSkippedRowsModal] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [showingDropdownOpen, setShowingDropdownOpen] = useState(false);

  // ==================== Project/Client Filter State ====================
  const [filter, setFilter] = useState("");
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectStatusFilter, setProjectStatusFilter] = useState("Active");
  const [isProjectStatusDropdownOpen, setIsProjectStatusDropdownOpen] = useState(false);
  const [tagQuery, setTagQuery] = useState("");

  // ==================== User Filter State ====================
  const [selectedUser, setSelectedUser] = useState(user?._id || null);
  // Redux may still be rehydrating on first render, leaving `user` null. The
  // useState initializer never runs again, so seed selectedUser once the auth
  // user arrives — otherwise every timer query stays gated off by `enabled`.
  useEffect(() => {
    if (!selectedUser && user?._id) {
      setSelectedUser(user._id);
    }
  }, [user, selectedUser]);
  const [userStatusFilter, setUserStatusFilter] = useState("Active");
  const [isUserStatusDropdownOpen, setIsUserStatusDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // ==================== Pagination State ====================
  const [selectedPage, setSelectedPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // ==================== Loading & Status State ====================
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomeTimeLoading, setIsCustomeTimeLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [isBulkImportLoading, setIsBulkImportLoading] = useState(false);

  // ==================== Bulk Import State ====================
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportFileName, setBulkImportFileName] = useState("");
  const [skippedRowsList, setSkippedRowsList] = useState([]);
  const [trackedEntriesCount, setTrackedEntriesCount] = useState(0);

  // ==================== Autocomplete & Filtered Data State ====================
  const [filteredData, setFilteredData] = useState([]);
  const [letterCount, setLetterCount] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ==================== Time Picker Dropdown State ====================
  const [timePickerOpen, setTimePickerOpen] = useState({
    start24: false,
    end24: false,
    start12: false,
    end12: false,
  });
  const [timePickerFilter, setTimePickerFilter] = useState({
    start24: "",
    end24: "",
    start12: "",
    end12: "",
  });
  const [timePickerSelection, setTimePickerSelection] = useState({
    start24: { hour: null, minute: null, ampm: null },
    end24: { hour: null, minute: null, ampm: null },
    start12: { hour: null, minute: null, ampm: null },
    end12: { hour: null, minute: null, ampm: null },
  });
  // Flag to prevent scrolling when value is set from dropdown click
  const isSettingFromDropdownRef = useRef(false);
  const justOpenedDropdownRef = useRef({ start24: false, end24: false, start12: false, end12: false });
  const timePickerRefs = {
    start24: useRef(null),
    end24: useRef(null),
    start12: useRef(null),
    end12: useRef(null),
  };

  // ==================== Refs ====================
  const datePickerRef = useRef();
  const manualDateContainerRef = useRef(null);
  const timerDateContainerRef = useRef(null);
  const timerCalendarPortalRef = useRef(null);
  const autoFillDropdownRef = useRef(null);
  const timerBarRef = useRef(null);
  const showingDropdownRef = useRef(null);
  const projectDropdownRef = useRef(null);
  const tagDropdownRef = useRef(null);
  const trackerOuterRef2 = useRef(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const start12HrTimeInputRef = useRef(null);
  const end12HrTimeInputRef = useRef(null);
  const projectStatusDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const userStatusDropdownRef = useRef(null);
  const componentRef = useRef(null);
  const [isTimerDatePickerOpen, setIsTimerDatePickerOpen] = useState(false);

  // ==================== Render Flags ====================
  // Use refs with mutable objects to persist render flags across renders (mutated by TimerList)
  const lastWeekRendered = useRef({ value: false });
  const thisWeekRendered = useRef({ value: false });

  useEffect(() => {
    setSplitted(false);
    setSplittedDate([]);
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Use a debounced function to optimize resize event handling
    const debouncedResize = debounce(handleResize, 300);

    window.addEventListener("resize", debouncedResize);

    // Perform initial state setup outside the resize logic
    setEndTimeManual24(DEFAULT_END_TIME_24HR);

    // Cleanup function to remove the event listener when component unmounts
    return () => {
      window.removeEventListener("resize", debouncedResize);
    };
  }, []);


  useEffect(() => {
    try {
      const rTask = getRunningTaskFromStorage(timerRedux);
      if (rTask) {
        setTrackTask(rTask);
      }
    } catch (e) {
      console.error("Error parsing runningTask from localStorage:", e);
      setTrackTask(null);
    }
  }, []);

  useEffect(() => {
    try {
      if (eventDetails?.title && eventDetails?.start && eventDetails?.end) {
        setTaskName(eventDetails.title);
        const startMoment = moment(eventDetails.start);
        const endMoment = moment(eventDetails.end);
        if (!startMoment.isValid() || !endMoment.isValid()) {
          console.error("Invalid date/time in eventDetails:", eventDetails);
          setUManualStartTimeError(true);
          setUManualEndTimeError(true);
          setUManualFormatedTimeError(true);
          return;
        }
        const is24Hour = appSettings.timeFormat === 24;
        const startTimeFormatted = is24Hour ? startMoment.format('HH:mm') : startMoment.format('hh:mm A');
        const endTimeFormatted = is24Hour ? endMoment.format('HH:mm') : endMoment.format('hh:mm A');
        setStartTimeManual24(startTimeFormatted);
        setEndTimeManual24(endTimeFormatted);
        setStartTimeManual(startTimeFormatted);
        setEndTimeManual(endTimeFormatted);
        setDate(startMoment.toDate());
        setCustomeTime(true);
        const timeDifferenceInSeconds = endMoment.diff(startMoment, 'seconds');
        if (timeDifferenceInSeconds > 0) {
          setUDuration(parseInt(timeDifferenceInSeconds));
          setUFormatedTimeManual(
            `${formatTime(Math.floor(timeDifferenceInSeconds / 3600))}:${formatTime(
              Math.floor((timeDifferenceInSeconds % 3600) / 60)
            )}:${formatTime(timeDifferenceInSeconds % 60)}`
          );
          setUManualStartTimeError(false);
          setUManualEndTimeError(false);
          setUManualFormatedTimeError(false);
        } else {
          console.warn("End time is before start time.");
          setUManualStartTimeError(true);
          setUManualEndTimeError(true);
          setUManualFormatedTimeError(true);
        }
      }
    } catch (error) {
      console.error("Error in eventDetails useEffect:", error);
      setUManualStartTimeError(true);
      setUManualEndTimeError(true);
      setUManualFormatedTimeError(true);
    }
  }, [eventDetails, appSettings.timeFormat]);

  // Helper functions are now imported from utils/trackerHelpers.js

  const handleDateIconClick = () => {
    setIsTimerDatePickerOpen(true);
    handleDateIconClickHelper(datePickerRef);
  };

  // Close calendar only when clicking outside the popup / date field
  useEffect(() => {
    if (!isTimerDatePickerOpen) return;

    const isInsideDatePicker = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest(".rs-picker-date-menu") ||
        target.closest(".rs-picker-daterange-menu") ||
        target.closest(".rs-picker-toggle-active") ||
        target.closest(".rs-picker-open") ||
        target.closest(".custom-date-icon") ||
        target.closest(".custoom-icon-calender")
      );
    };

    const handleOutsidePointer = (event) => {
      if (isInsideDatePicker(event.target)) return;
      try {
        if (datePickerRef?.current && typeof datePickerRef.current.close === "function") {
          datePickerRef.current.close();
        }
      } catch (error) {
        console.error("Error while closing the date picker:", error);
      }
      setIsTimerDatePickerOpen(false);
    };

    document.addEventListener("mousedown", handleOutsidePointer, true);
    document.addEventListener("touchstart", handleOutsidePointer, { capture: true, passive: true });

    return () => {
      document.removeEventListener("mousedown", handleOutsidePointer, true);
      document.removeEventListener("touchstart", handleOutsidePointer, true);
    };
  }, [isTimerDatePickerOpen]);

  const { data: tagOptions = [] } = useTagDetails(token);
  const { data: clientData = [] } = useClientProjectDetails(token);

  // Pagination state for grouped timer data (used for autoComplete)
  // Using a higher limit (50) for autoComplete to work well with client-side filtering
  const [groupedTimerPage, setGroupedTimerPage] = useState(1);
  const [groupedTimerLimit] = useState(50); // Higher limit for autoComplete

  const {
    data: groupedTimerResponse = { data: [], pagination: {} },
    isLoading: isGroupedTimerDataLoading,
    refetch: refetchGroupedTimerData
  } = useGroupedTimerData(token, selectedUser, groupedTimerPage, groupedTimerLimit);

  // Extract data array from paginated response - memoized to prevent unnecessary re-renders
  // Return the array reference directly from the response to maintain React Query's reference stability
  const groupedTimerData = useMemo(() => {
    if (Array.isArray(groupedTimerResponse?.data)) {
      return groupedTimerResponse.data;
    }
    if (Array.isArray(groupedTimerResponse)) {
      return groupedTimerResponse;
    }
    return [];
  }, [groupedTimerResponse]);

  // Pagination state for timer data (main timer list)
  const [timerPage, setTimerPage] = useState(1);
  const [timerLimit, setTimerLimit] = useState(10); // Default limit matches perPage state

  // Sync selectedPage with timerPage (selectedPage is 0-based, timerPage is 1-based)
  // This ensures selectedPage stays in sync when timerPage changes from other sources
  useEffect(() => {
    const expectedSelectedPage = timerPage - 1;
    // Only update if different to avoid unnecessary re-renders
    if (selectedPage !== expectedSelectedPage) {
      setSelectedPage(expectedSelectedPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerPage]); // Intentionally only depend on timerPage to avoid loops

  const {
    data: timerResponse = { data: [], pagination: {} },
    isLoading: isTimerDataLoading,
    isFetching: isTimerDataFetching,
    refetch: refetchTimerData
  } = useTimerData(token, selectedUser, timerPage, timerLimit);

  // Use isFetching for pagination loader (shows during page changes)
  // Use isLoading for initial load (shows when no data exists)
  const isTimerLoading = isTimerDataLoading || isTimerDataFetching;

  // Extract timer data array and pagination info
  const timer = Array.isArray(timerResponse?.data) ? timerResponse.data : [];
  const timerPagination = timerResponse?.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 };
  const { data: timeZoneData = [], isLoading: isTimeZoneDataLoading, refetch: refetchTimeZoneDatas } = useTimeZone(user && user._id, token);
  const { data: userProfiles = [], isLoading: isUserProfilesLoading } = useFetchUsersTimer(token);

  // Create user helpers using factory function
  const {
    toggleUserStatusDropdown,
    handleUserStatusChange,
    handleUserSearchChange,
    toggleUserDropdown,
    handleUserSelect,
  } = useMemo(
    () =>
      createUserHelpers({
        isUserStatusDropdownOpen,
        isUserDropdownOpen,
        setIsUserStatusDropdownOpen,
        setUserSearchTerm,
        setUserStatusFilter,
        setSelectedUser,
        setIsUserDropdownOpen,
      }),
    [isUserStatusDropdownOpen, isUserDropdownOpen]
  );

  // Memoize filtered users to avoid recalculating on every render
  const filteredUsers = useMemo(
    () => filterUsers(userProfiles, userSearchTerm),
    [userProfiles, userSearchTerm]
  );

  useEffect(() => {
    const handleLoading = async () => {
      try {
        await dispatch(setAppLoading(isTimerDataLoading));
      } catch (error) {
        console.error("Error setting app loading state:", error);
      }
    };

    handleLoading();
  }, [dispatch, isTimerDataLoading]);

  useEffect(() => {
    const updateLetterCount = () => {
      try {
        const content = trackerOuterRef2?.current?.innerText;
        const count = content ? content.replace(/\s/g, "").length : 0; // Remove whitespaces
        setLetterCount(count);
      } catch (error) {
        console.error("Error updating letter count:", error);
        setLetterCount(0); // Optional fallback
      }
    };

    try {
      // Call the function initially
      updateLetterCount();

      // Use setTimeout for non-blocking update
      const timeoutId = setTimeout(() => {
        updateLetterCount();
      }, 0);

      // Cleanup function
      return () => {
        clearTimeout(timeoutId);
      };
    } catch (error) {
      console.error("Error in useEffect (letter count):", error);
    }
  }, []);


  useEffect(() => {
    // Reset to page 1 when user changes
    setGroupedTimerPage(1);
    setTimerPage(1);
  }, [selectedUser]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          refetchTimerData(),
          refetchGroupedTimerData()
        ]);
      } catch (error) {
        console.error('Error fetching timer or grouped data:', error);
      }
    };

    try {
      fetchData();
    } catch (error) {
      console.error('Unexpected error in useEffect (fetchData):', error);
    }
  }, [selectedUser, groupedTimerPage, timerPage, timerLimit, refetchGroupedTimerData, refetchTimerData]);


  useEffect(() => {
    const checkAndFetchTimeZoneData = async () => {
      try {
        if (user && !isTimeZoneDataLoading) {
          // Show the modal if time zone or currency is not set
          if (
            timeZoneData === null ||
            timeZoneData.currency === null ||
            timeZoneData.timeZone === null
          ) {
            setShowTimeZoneModal(true);
          }
        }

        // Non-blocking call to refetch time zone data
        await refetchTimeZoneDatas();
      } catch (error) {
        console.error('Error in checkAndFetchTimeZoneData:', error);
      }
    };

    try {
      setTimeout(() => {
        checkAndFetchTimeZoneData().catch((error) => {
          console.error('Unhandled promise rejection in setTimeout:', error);
        });
      }, 0);
    } catch (error) {
      console.error('Unexpected error in useEffect:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]); // timezone config doesn't need to re-check on every timer operation

  // Memoize filtered tag items to avoid recalculating on every render
  const filteredTagItems = useMemo(
    () => filterTagItems(tagOptions, tagQuery),
    [tagOptions, tagQuery]
  );

  // Memoize filtered client data to avoid recalculating on every render
  const filteredClientData = useMemo(
    () => filterClientData(clientData, filter, projectStatusFilter),
    [clientData, filter, projectStatusFilter]
  );

  // Create Redux actions object once
  const timerReduxActions = useMemo(() => createTimerReduxActions(), []);

  // Simple event handlers using helper functions
  const handleProjectStatusChange = useMemo(
    () => createStatusChangeHandler(
      setProjectStatusFilter,
      () => {
        setSelectedClients(new Set());
        setFilter(""); // Clear the filter input when status changes
      }
    ),
    []
  );

  const toggleProjectStatusDropdown = useMemo(
    () => createToggleHandler(setIsProjectStatusDropdownOpen),
    []
  );

  const handleTagInputChange = useMemo(
    () => createInputChangeHandler(setTagQuery, setTag),
    []
  );

  const handleInputClick = useMemo(
    () => createToggleHandler(setDropdownVisible),
    []
  );


  // Create data initialization handler using factory function
  const { initializeData, handleEscapeKey } = useMemo(
    () =>
      createDataInitializationHandler({
        timeTrackerLog,
        eventDetails,
        setTag,
        setTagId,
        setTaskName,
        setStartTime,
        setIsRunning,
        setProjectName,
        setClient,
        setClientId,
        setProjectId,
        setSprintName,
        setSprintId,
        setIsBillable,
        refetchGroupedTimerData,
        refetchTimerData,
      }),
    [timeTrackerLog, eventDetails, refetchGroupedTimerData, refetchTimerData]
  );

  useEffect(() => {
    setTimeout(() => {
      initializeData();
    }, 0);

    const handleKeyDown = (event) =>
      handleEscapeKey(
        event,
        {
          setProjectDropdownOpen,
          setTagDropdownOpen,
          setCustomeTime,
          setDropdownVisible,
          setTaskError,
          setSplitted,
          setSplittedDate,
          setTaskName,
        },
        dispatch,
        eventDetailReset,
        eventDetails
      );

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [stopRunning, timerUpdate, timerData, initializeData, handleEscapeKey, dispatch, eventDetailReset]);

  const handleStartClick = async () => {
    try {
      setStopLoading(false);
      setTrigger(true);

      const time = getCurrentTime();

      // Validate data using service function
      const validation = validateTimerStartData({
        taskName,
        projectName,
        projectId,
        client,
        sprintId
      });

      if (validation.isValid) {
        try {
          setTaskError(false);
          setIsRunning(true);
          setStartTime(time);

          // Persist timer start data to localStorage and Redux
          persistTimerStartData({
            dispatch,
            time,
            taskName,
            projectName,
            projectId,
            tag,
            tagId,
            client,
            clientId,
            sprintName,
            sprintId,
            isBillable,
          });

          // Start timer via API call
          await startTimerApiCall({
            token,
            time,
            taskName,
            projectName,
            projectId,
            tag,
            tagId,
            client,
            clientId,
            isBillable,
            sprintName,
            sprintId,
            selectedUser,
            dispatch,
            setTrackTask,
            toast,
            toastConfig,
          });

          dispatch(setTimerData(Date.now()));
          toast.success("Timer started", toastConfig);
        } catch (apiError) {
          console.error("Error starting timer:", apiError);
          toast.error("Failed to start timer. Please try again.", toastConfig);
        }

      } else {
        setTaskError(true);
        if (validation.errors.length > 0) {
          toast.warn(`Please select ${validation.errors.join(", ")}`, toastConfig);
        }
      }
    } catch (error) {
      console.error("Unexpected error in handleStartClick:", error);
      toast.error("An unexpected error occurred while starting the timer.", toastConfig);
    } finally {
      setTrigger(false);
    }
  };

  const handleEndClick = async () => {
    // Early return if no timer is running
    if (!isRunning) {
      return;
    }

    setStopLoading(true);

    try {
      const localTime = getCurrentTime();

      // Get running task with better error handling
      const rTask = getRunningTaskFromStorage(timerRedux);
      if (!rTask) {
        handleError(new Error("No running task found"), "stopping timer", { action: "Reading timer data" });
        showErrorToast(new Error("No running task found"), "reading timer data", toast.error);
        setStopLoading(false);
        return;
      }

      if (rTask) {
        setTrackTask(rTask);
      }

      const runTask = rTask || trackTask;

      // Validate data using service function
      const validation = validateTimerStopData({
        user,
        taskName,
        projectId,
        projectName,
        clientId,
        client,
        sprintId,
        sprintName,
        isBillable
      });

      if (!validation.isValid) {
        validation.errors.forEach(error => toast.error(error, toastConfig));
        setStopLoading(false);
        return;
      }

      // Build post data using service function
      const { postData, apiUrl } = buildTimerStopPostData({
        startTime,
        endTime: localTime,
        user,
        taskName,
        projectName,
        projectId,
        tag,
        tagId,
        client,
        clientId,
        isBillable,
        sprintName,
        sprintId,
        runTask
      });

      // API Call with improved error handling
      try {
        await axios.post(apiUrl, postData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (apiError) {
        handleApiError(apiError, "stopping timer", toast.error);
        setStopLoading(false);
        return;
      }

      // Cleanup state and localStorage
      try {
        // Reset local state
        setIsRunning(false);
        setTaskName("");
        setTag(null);
        setTagId(null);
        setProjectName(null);
        setProjectId(null);
        setSprintName("");
        setSprintId("");

        // Clear storage using shared action map (avoids repeating an actions object here)
        clearTimerState(dispatch, timerReduxActions);

        dispatch(setTimerData(Date.now()));
        setStopRunning((prev) => !prev);
        refetchTimerData();

        toast.success("Timer stopped successfully", toastConfig);

      } catch (cleanupError) {
        handleError(cleanupError, "timer cleanup", { action: "Cleaning up timer state" });
        showErrorToast(cleanupError, "timer cleanup", toast.error, { autoClose: 3000 });
      }

    } catch (err) {
      handleError(err, "stopping timer", { operation: "handleEndClick" });
      showErrorToast(err, "stopping timer", toast.error);
    } finally {
      setStopLoading(false);
    }
  };

  // Helper function to get running task from storage (now imported from utils)

  // Helper function to start timer after validation
  const startTimerAfterValidation = async ({
    task,
    project_id,
    projectName,
    tag_name,
    tag_id,
    client,
    clientId,
    sprint_name,
    sprint_id,
    billable,
  }) => {
    // Validate timer resume data
    const validation = validateTimerResumeData({
      task,
      projectName,
      projectId: project_id,
      client,
      clientId,
      sprintName: sprint_name,
      sprintId: sprint_id,
    });

    if (!validation.isValid) {
      setResumeLoading(false);
      setTaskError(true);
      const errorMessage = `Please select ${validation.errors.map(e => `'${e}'`).join(", ")}`;
      toast.error(errorMessage, toastConfig);
      return false;
    }

    try {
      // Start new timer using service function
      const result = await startNewTimer({
        token,
        task,
        projectName,
        projectId: project_id,
        tagName: tag_name,
        tagId: tag_id,
        client,
        clientId,
        sprintName: sprint_name,
        sprintId: sprint_id,
        billable,
        selectedUser,
      });

      // Update local state
      setTaskName(task);
      setProjectName(projectName);
      setProjectId(project_id);
      setTag(tag_name);
      setTagId(tag_id);
      setSprintName(sprint_name);
      setSprintId(sprint_id);
      setClient(client);
      setClientId(clientId);
      setIsBillable(billable);
      setTaskError(false);
      setIsRunning(true);
      setStartTime(result.startTime);

      // Sync state to storage
      syncTimerStateToStorage(
        {
          task,
          projectName,
          projectId: project_id,
          tagName: tag_name,
          tagId: tag_id,
          client,
          clientId,
          sprintName: sprint_name,
          sprintId: sprint_id,
          isBillable: billable,
          startTime: result.startTime,
          isRunning: true,
          runningTaskId: result.id,
        },
        dispatch,
        timerReduxActions
      );

      dispatch(setTimerData(Date.now()));
      return true;
    } catch (error) {
      handleApiError(error, "starting timer", toast.error);
      return false;
    } finally {
      setResumeLoading(false);
    }
  };

  const resumeTimeTracking = async (
    task,
    project_id,
    projectName,
    tag_name,
    tag_id,
    client,
    clientId,
    date,
    startTime,
    dupendTime, // Unused but required by interface from CardBody
    duration, // Unused but required by interface from CardBody
    sprint_name,
    sprint_id,
    _id, // Unused but required by interface from CardBody
    billable
  ) => {
    setTrigger(true);
    setResumeLoading(true);

    try {
      // If timer is already running, stop it first
      if (isRunning) {
        try {
          setStopLoading(true);
          const runTask = getRunningTaskFromStorage(timerRedux);

          if (!runTask) {
            handleError(new Error("No running task found"), "resuming timer", {
              action: "Stopping current timer",
            });
            showErrorToast(
              new Error("No active timer found to stop"),
              "resuming timer",
              toast.error
            );
            setResumeLoading(false);
            setTrigger(false);
            return;
          }

          // Stop current timer using service function
          await stopCurrentTimer({
            token,
            selectedUser,
            taskName,
            projectName,
            projectId,
            tagName: tag_name,
            tagId: tag_id,
            client,
            clientId,
            sprintName: sprint_name,
            sprintId: sprint_id,
            isBillable,
            runningTask: runTask,
          });

          // Clear current timer state
          clearTimerState(dispatch, timerReduxActions);

          setIsRunning(false);
          setStopLoading(false);
          setTaskName("");
          setTag(null);
          setTagId(null);
          setProjectName(null);
          setProjectId(null);
          setSprintName("");
          setSprintId("");
          dispatch(setTimerData(Date.now()));
          refetchTimerData();

        } catch (stopError) {
          handleApiError(stopError, "stopping current timer", toast.error);
          setResumeLoading(false);
          setTrigger(false);
          return;
        }
      }

      // Start new timer
      const success = await startTimerAfterValidation({
        task,
        project_id,
        projectName,
        tag_name,
        tag_id,
        client,
        clientId,
        sprint_name,
        sprint_id,
        billable,
      });

      if (!success) {
        setTrigger(false);
        return;
      }

    } catch (err) {
      handleError(err, "resuming timer", { operation: "resumeTimeTracking" });
      showErrorToast(err, "resuming timer", toast.error);
    } finally {
      setTrigger(false);
      setResumeLoading(false);
    }
  };

  // Create custom time submission handler using factory function
  const { handleSetCustomeTimes } = useMemo(
    () =>
      createCustomTimeSubmissionHandler({
        taskName,
        projectName,
        projectId,
        date,
        client,
        clientId,
        sprintId,
        tagId,
        isBillable,
        selectedUser,
        startTimeManual,
        startTimeManual24,
        endTimeManual,
        endTimeManual24,
        uFormatedTimeManual,
        uDuration,
        splitted,
        splittedDate,
        eventDetails,
        appSettings,
        startInputRef,
        endInputRef,
        start12HrTimeInputRef,
        end12HrTimeInputRef,
        setIsTimeInputActive,
        setTaskError,
        setUDuration,
        setUFormatedTimeManual,
        setIsLoading,
        setIsCustomeTimeLoading,
        setIsRunning,
        setCustomeTime,
        setTag,
        setTagId,
        setDate,
        setProjectName,
        setProjectId,
        setTaskName,
        setStopRunning,
        setTimerUpdate,
        setSprintName,
        setSprintId,
        setStartTimeManual,
        setEndTimeManual,
        setStartTimeManual24,
        setEndTimeManual24,
        setSplitted,
        setSplittedDate,
        setUManualStartTimeError,
        setUManualEndTimeError,
        setUManualFormatedTimeError,
        dispatch,
        eventDetailReset,
        setTimerData,
        token,
        toast,
      }),
    [
      taskName,
      projectName,
      projectId,
      date,
      client,
      clientId,
      sprintId,
      tagId,
      isBillable,
      selectedUser,
      startTimeManual,
      startTimeManual24,
      endTimeManual,
      endTimeManual24,
      uFormatedTimeManual,
      uDuration,
      splitted,
      splittedDate,
      eventDetails,
      appSettings,
    ]
  );

  // Memoize start time to avoid reading from storage on every render
  // Update when timerData changes to reflect current timer status (timerData updates when timer starts/stops)
  const start = useMemo(() => secureGetItem(STORAGE_KEYS.START_TIME, null), [timerData]);

  // Create timer mode handlers using factory function
  const { handleManualClick, handleTimerClick } = useMemo(
    () =>
      createTimerModeHandlers({
        projectDropdownOpen,
        tagDropdownOpen,
        uManualStartTimeError,
        uManualEndTimeError,
        uManualFormatedTimeError,
        setCustomeTime,
        setDate,
        setProjectDropdownOpen,
        setTagDropdownOpen,
        setSplitted,
        setSplittedDate,
        setUManualStartTimeError,
        setUManualEndTimeError,
        setUManualFormatedTimeError,
        setStartTimeManual,
        setEndTimeManual,
        setStartTimeManual24,
        setEndTimeManual24,
        setUFormatedTimeManual,
        setTaskError,
      }),
    [projectDropdownOpen, tagDropdownOpen, uManualStartTimeError, uManualEndTimeError, uManualFormatedTimeError]
  );

  const autoLoad = (
    task,
    project_id,
    projectName,
    tag_name,
    tag_id,
    client,
    clientId,
    sprint_name,
    sprint_id,
    _id,
    billable
  ) => {
    autoLoadTimerData({
      task,
      project_id,
      projectName,
      tag_name,
      tag_id,
      client,
      clientId,
      sprint_name,
      sprint_id,
      _id,
      billable,
      setters: {
        setTaskName,
        setProjectName,
        setProjectId,
        setTag,
        setTagId,
        setClient,
        setClientId,
        setSprintName,
        setSprintId,
        setIsBillable,
        setDropdownVisible,
      },
    });
  };

  // Initialize filteredData only once when groupedTimerData first becomes available
  // Use a ref to track if we've initialized to prevent infinite loops
  const hasInitializedFilteredData = useRef(false);

  // Create filtered data initialization handler using factory function
  const initializeFilteredData = useMemo(
    () =>
      createFilteredDataInitializationHandler({
        groupedTimerData,
        setFilteredData,
        hasInitializedFilteredDataRef: hasInitializedFilteredData,
      }),
    [groupedTimerData]
  );

  useEffect(() => {
    initializeFilteredData();
  }, [initializeFilteredData]);

  // Create click outside dropdown handler using factory function
  const { handleClickOutsideDropdown, checkIfInsideTimePicker } = useMemo(
    () =>
      createClickOutsideDropdownHandler({
        showingDropdownRef,
        autoFillDropdownRef,
        projectStatusDropdownRef,
        projectDropdownRef,
        tagDropdownRef,
        userDropdownRef,
        userStatusDropdownRef,
        timePickerRefs,
        startInputRef,
        endInputRef,
        start12HrTimeInputRef,
        end12HrTimeInputRef,
        setShowingDropdownOpen,
        setDropdownVisible,
        setIsProjectStatusDropdownOpen,
        setProjectDropdownOpen,
        setTagDropdownOpen,
        setIsUserDropdownOpen,
        setIsUserStatusDropdownOpen,
        setTimePickerOpen,
      }),
    [] // Refs and setters are stable, no dependencies needed
  );

  useEffect(() => {
    try {
      // Use mousedown instead of click to avoid conflicts with input focus
      const handleClick = (event) => {
        // Don't close if clicking on any time input or time picker dropdown
        const isClickOnTimeInput =
          startInputRef.current?.contains(event.target) ||
          endInputRef.current?.contains(event.target) ||
          start12HrTimeInputRef.current?.contains(event.target) ||
          end12HrTimeInputRef.current?.contains(event.target);

        const isClickInsideAnyTimePicker = checkIfInsideTimePicker(event.target, timePickerRefs);

        if (!isClickOnTimeInput && !isClickInsideAnyTimePicker) {
          // Run synchronously so containment is checked before any dropdown
          // closes (e.g. status option click); otherwise event.target can be
          // detached and ref.current.contains(event.target) is false.
          handleClickOutsideDropdown(event);
        }
      };
      document.addEventListener("mousedown", handleClick, true); // Use capture phase
      return () => {
        document.removeEventListener("mousedown", handleClick, true);
      };
    } catch (error) {
      console.error("Error adding click event listener:", error);
    }
  }, [handleClickOutsideDropdown, checkIfInsideTimePicker]);

  // Create manual time change handlers using factory function
  // Setters from useState are stable references and don't need to be in dependencies
  const {
    manualChangeStartTime,
    manualChangeStartTime24,
    manualChangeEndTime,
    manualChangeEndTime24,
  } = useMemo(
    () =>
      createManualTimeHandlers({
        setStartTimeManual,
        setEndTimeManual,
        setStartTimeManual24,
        setEndTimeManual24,
        setStartTimeTemp,
        setEndTimeTemp,
        setUDuration,
        setUFormatedTimeManual,
        setUManualStartTimeError,
        setUManualEndTimeError,
        setUManualFormatedTimeError,
        setSplitted,
        setSplittedDate,
        endTimeManual,
        startTimeManual,
        splitted,
        splittedDate,
      }),
    // Only include actual state values that affect handler behavior
    [endTimeManual, startTimeManual, splitted, splittedDate]
  );


  // Create time input validation handler using factory function
  const handleTimeInputClickOutside = useMemo(
    () =>
      createTimeInputValidationHandler({
        startInputRef,
        endInputRef,
        start12HrTimeInputRef,
        end12HrTimeInputRef,
        startTimeManual,
        endTimeManual,
        startTimeTemp,
        endTimeTemp,
        manualChangeStartTime24,
        manualChangeEndTime24,
        manualChangeStartTime,
        manualChangeEndTime,
      }),
    [
      startTimeManual,
      endTimeManual,
      startTimeTemp,
      endTimeTemp,
      manualChangeStartTime,
      manualChangeStartTime24,
      manualChangeEndTime,
      manualChangeEndTime24,
    ]
  );

  useEffect(() => {
    try {
      document.body.addEventListener("click", handleTimeInputClickOutside);

      return () => {
        document.body.removeEventListener("click", handleTimeInputClickOutside);
      };
    } catch (error) {
      console.error("Error in useEffect for click outside handling:", error);
    }
  }, [handleTimeInputClickOutside]);


  // Create manual time show edit handlers using factory function
  const {
    manualTimeShowEdit,
    manualTimeShowEdit24,
  } = useMemo(
    () =>
      createManualTimeShowEditHandlers({
        startTimeManual,
        startTimeManual24,
        date,
        setUFormatedTimeManual,
        setUManualFormatedTimeError,
        setUManualStartTimeError,
        setUManualEndTimeError,
        setStartTimeManual,
        setEndTimeManual,
        setEndTimeManual24,
        setSplittedDate,
        setSplitted,
        setUDuration,
      }),
    [startTimeManual, startTimeManual24, date]
  );

  // Memoize task input onChange handler for manual time section
  const handleManualTaskInputChange = useMemo(
    () => (e) => {
      setTaskName(e.target.value);
      setTaskError(false);
      const filterText = e.target.value.toLowerCase();
      const filteredItems = groupedTimerData.filter(
        (item) =>
          item.task.toLowerCase().includes(filterText)
      );
      setFilteredData(filteredItems);
      // Typing must reopen the dropdown — click alone toggles it shut
      setDropdownVisible(true);
    },
    [groupedTimerData]
  );

  // Memoize selected user profile to avoid repeated find() calls
  const selectedUserProfile = useMemo(
    () => userProfiles.find((profile) => profile._id === selectedUser),
    [userProfiles, selectedUser]
  );

  // Cache current user display name to localStorage so it's available during next load
  useEffect(() => {
    if (selectedUserProfile && selectedUserProfile._id === user?._id) {
      const name = `${selectedUserProfile.first_name || selectedUserProfile.company_first_name || ""} ${selectedUserProfile.last_name || selectedUserProfile.company_last_name || ""}`.trim();
      if (name) localStorage.setItem('tracker_my_display_name', name);
    }
  }, [selectedUserProfile, user]);

  const myDisplayName = localStorage.getItem('tracker_my_display_name') || "";

  useEffect(() => {
    if (!taskError) return;
    const handleClickOutside = (e) => {
      if (timerBarRef.current && !timerBarRef.current.contains(e.target)) {
        setTaskError(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [taskError]);

  // Memoize admin check to avoid repeated calculations
  const isUserAdmin = useMemo(() => isAdminUser(user), [user]);

  // Memoize DatePicker value to avoid recalculating on every render
  const datePickerValue = useMemo(() => {
    try {
      // The DatePicker shows a CALENDAR DAY and renders whatever Date it is
      // given in the browser's zone. Rebuilding the day as tenant midnight
      // produced an instant (Oct 8 00:00 IST == Oct 7 18:30Z) that the picker
      // then displayed as the previous day for anyone west of the tenant.
      // Hand it a plain local-midnight Date for the day being edited; the save
      // path reads those same Y/M/D parts.
      const d = new Date(date);
      if (isNaN(d.getTime())) throw new Error("invalid date");
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    } catch (error) {
      console.error("Error calculating datePickerValue:", error);
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }
  }, [date]);

  // Memoize shouldDisableDate function to avoid recreating on every render
  const shouldDisableDate = useMemo(
    () => (dateToCheck) => {
      try {
        const formattedDate = moment(dateToCheck)
          .tz(global.config.getTimeZone())
          .format("YYYY-MM-DD HH:mm:ss");

        const lockHoursResult = getLockHours(
          formattedDate,
          global.config.getTimeZone(),
          "YYYY-MM-DD HH:mm:ss",
          user
        );

        return lockHoursResult === false;
      } catch (error) {
        console.error("Error in shouldDisableDate:", error);
        return false;
      }
    },
    [user]
  );

  const timerDateSection = (
    <div className="date-overview-sec timer-mode-date-sec" ref={timerDateContainerRef}>
      <div
        onClick={handleDateIconClick}
        className="custom-date-icon"
      >
        <DatePickerIconSVG />
      </div>
      <DatePicker
        ref={datePickerRef}
        value={datePickerValue}
        onChange={(value) => {
          try {
            const convertedDate = moment.tz(value, global.config.getTimeZone()).toDate();
            setDate(convertedDate);
          } catch (error) {
            console.error("Error in DatePicker onChange:", error);
          }
        }}
        format="dd/MM/yyyy"
        className="timer-date time-date-manual"
        menuClassName="timer-outer-datepicker-menu"
        container={() =>
          timerCalendarPortalRef.current ||
          timerDateContainerRef.current?.closest(".timetracker-overview-outer") ||
          timerDateContainerRef.current ||
          document.body
        }
        placement="bottomEnd"
        oneTap
        isoWeek={appSettings.weekStartDay === "Monday"}
        shouldDisableDate={shouldDisableDate}
        onEnter={() => setIsTimerDatePickerOpen(true)}
        onExit={() => setIsTimerDatePickerOpen(false)}
        onOpen={() => setIsTimerDatePickerOpen(true)}
        onClose={() => setIsTimerDatePickerOpen(false)}
      />
    </div>
  );

  // Use ref for DatePicker container to avoid querying DOM on every render
  // Note: DatePicker container is queried when needed since element may not exist on initial render

  // Date calculation functions are now imported from helpers/dateCalculations.js

  const handlePageChange = (selected) =>
    handlePageChangeHelper({
      selected,
      isTimerLoading,
      setSelectedPage,
      setTimerPage,
    });

  // Sync timerLimit with perPage when user changes perPage
  useEffect(() => {
    setTimerLimit(perPage);
    setTimerPage(1); // Reset to first page when limit changes
  }, [perPage]);

  // Use server-side pagination data
  const totalResults = timerPagination.total || timer.length;
  const pageCount = timerPagination.totalPages || Math.ceil(timer.length / perPage);
  // No need for client-side slicing - server already paginated
  const currentPageData = timer;

  // Memoize date calculations to avoid recalculating on every render
  const {
    highestDate,
    leastDate,
    highestLastWeekDate,
    leastLastWeekDate,
    extremesByWeek,
  } = useMemo(() => {
    const dateArray = currentPageData.map((item) => moment.tz(item[0], "YYYY-MM-DD", global.config.getTimeZone()).toDate());
    const thisWeekExtremes = filterAndFindExtremes(dateArray, appSettings);
    const lastWeekExtremes = findExtremesOfLastWeek(dateArray, appSettings);
    return {
      highestDate: thisWeekExtremes.highestDate,
      leastDate: thisWeekExtremes.leastDate,
      highestLastWeekDate: lastWeekExtremes.highestLastWeekDate,
      leastLastWeekDate: lastWeekExtremes.leastLastWeekDate,
      extremesByWeek: findExtremesByWeek(dateArray, appSettings),
    };
  }, [currentPageData, appSettings]);

  // Reset render flags when data changes
  useEffect(() => {
    lastWeekRendered.current.value = false;
    thisWeekRendered.current.value = false;
  }, [timer, selectedUser]);

  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);


  // Create time validation handler using factory function
  const validateTimeInputs = useMemo(
    () =>
      createTimeValidationHandler({
        startTimeManual24,
        endTimeManual24,
        eventDetails,
        setUManualFormatedTimeError,
        setUManualEndTimeError,
      }),
    [startTimeManual24, endTimeManual24, eventDetails]
  );

  useEffect(() => {
    validateTimeInputs();
  }, [validateTimeInputs]);

  // fadeIn is now imported from utils/constants.js
  // User dropdown handlers are now created via createUserHelpers above

  const handleUpload = async (event) => {
    event.preventDefault();
    await handleBulkImportUpload({
      bulkImportFile,
      token,
      setIsBulkImportLoading,
      setSkippedRowsList,
      setTrackedEntriesCount,
      setShowSkippedRowsModal,
      refetchTimerData,
      refetchGroupedTimerData,
      viewIsImport: props.viewIsImport,
      handleBulkImportCloseModal,
      toastConfigWithTheme,
    });
  };

  // Create bulk import modal helpers using factory function
  const {
    handleBulkImportOpenModal,
    handleBulkImportCloseModal,
  } = useMemo(
    () =>
      createBulkImportModalHelpers({
        setIsBulkImportModalOpen,
        setBulkImportFile,
        setBulkImportFileName,
      }),
    [] // Setters are stable references
  );

  const handleBulkImportFileChange = (e) => {
    handleBulkImportFileSelect(e, setBulkImportFile, setBulkImportFileName);
  };

  // ==================== Time Picker Helper Functions ====================
  // Functions are now imported from utils/timePickerUtils.js

  // isAnyTimeInputFocused is now imported from utils/trackerHelpers.js

  // scrollToTimePickerValue is now imported from utils/timePickerUtils.js

  // Initialize time picker selection when it opens - using custom hook
  useTimePickerInitialization({
    timePickerOpen,
    startTimeManual24,
    endTimeManual24,
    startTimeManual,
    endTimeManual,
    setTimePickerSelection,
  });

  // Time Picker Dropdown Component is now imported from trackerComponents/TimePickerDropdown.jsx

  // ==================== Constants and Reusable Components ====================
  // Style objects are now imported from utils/constants.js

  // SVG components are now imported from trackerComponents/SvgIcons.jsx

  // Create project dropdown helpers using factory function
  const {
    handleProjectDropdownToggle,
    renderProjectNameDisplay,
    renderNoItemsMessage,
    renderProjectStatusFilter,
    renderTagIconOrName,
    handleSprintSelect,
    projectMatchesFilter,
    handleProjectFilterChange,
    renderInlineTagDropdown,
    renderBillableIcon,
    renderProjectNameDisplaySmall,
    renderProjectDropdownWithFilter,
    renderProjectDropdownContent,
  } = useMemo(
    () =>
      createProjectDropdownHelpers({
        // State values
        projectName,
        sprintName,
        client,
        letterCount,
        projectStatusFilter,
        isProjectStatusDropdownOpen,
        projectDropdownOpen,
        filter,
        selectedClients,
        selectedProject,
        filteredClientData,
        tag,
        tagDropdownOpen,
        filteredTagItems,
        isBillable,
        user,
        // Refs
        trackerOuterRef2,
        projectStatusDropdownRef,
        tagDropdownRef,
        // Setters
        setProjectDropdownOpen,
        setProjectStatusFilter,
        setFilter,
        setSelectedClients,
        setSelectedProject,
        setTaskError,
        setTagDropdownOpen,
        setProjectName,
        setProjectId,
        setClient,
        setClientId,
        setIsBillable,
        setSprintId,
        setSprintName,
        setTag,
        setTagId,
        // Handlers
        toggleProjectStatusDropdown,
        handleProjectStatusChange,
        handleTagInputChange,
      }),
    [
      projectName,
      sprintName,
      client,
      letterCount,
      projectStatusFilter,
      isProjectStatusDropdownOpen,
      projectDropdownOpen,
      filter,
      selectedClients,
      selectedProject,
      filteredClientData,
      tag,
      tagDropdownOpen,
      filteredTagItems,
      isBillable,
      user,
    ]
  );

  // ==================== Main Render ====================
  // Timer Component Structure:
  // 1. Top Section: Manual/Timer toggle, User filter, Bulk import
  // 2. Timer Section: Task input, Project/Tag selectors, Timer controls (12hr/24hr format)
  // 3. Manual Time Section: Date picker, time inputs, duration display
  // 4. Timer List: Paginated list of time entries with week totals
  return (
    <motion.div
      ref={componentRef}
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      transition={{ duration: 0.3 }}
    >
      <div className="time-tracker-outer testash">
        <div className="top-section-tracker">
          <div className="manual-timer-change">
            <div className="anual-timer-change-outer">
              <div className="anual-timer-change-inner">
                <button
                  className={customeTime ? "active" : ""}
                  onClick={handleManualClick}
                  disabled={isRunning}
                >
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="23"
                      height="14"
                      viewBox="0 0 23 14"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4.51758 0.828167C4.51758 0.370783 4.83595 0 5.22868 0H22.0365C22.4292 0 22.7476 0.370783 22.7476 0.828167C22.7476 1.28555 22.4292 1.65633 22.0365 1.65633H5.22868C4.83595 1.65633 4.51758 1.28555 4.51758 0.828167Z"
                        fill="#96A8BA"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 0.828167C0 0.370783 0.370783 0 0.828167 0L2.33393 0C2.79131 0 3.16209 0.370783 3.16209 0.828167C3.16209 1.28555 2.79131 1.65633 2.33393 1.65633L0.828167 1.65633C0.370783 1.65633 0 1.28555 0 0.828167Z"
                        fill="#96A8BA"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 6.85118C0 6.39379 0.370783 6.02301 0.828167 6.02301L2.33393 6.02301C2.79131 6.02301 3.16209 6.39379 3.16209 6.85118C3.16209 7.30856 2.79131 7.67934 2.33393 7.67934L0.828167 7.67934C0.370783 7.67934 0 7.30856 0 6.85118Z"
                        fill="#96A8BA"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 12.8743C0 12.4169 0.370783 12.0461 0.828167 12.0461H2.33393C2.79131 12.0461 3.16209 12.4169 3.16209 12.8743C3.16209 13.3317 2.79131 13.7025 2.33393 13.7025H0.828167C0.370783 13.7025 0 13.3317 0 12.8743Z"
                        fill="#96A8BA"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4.51758 6.85118C4.51758 6.39379 4.83595 6.02301 5.22868 6.02301H22.0365C22.4292 6.02301 22.7476 6.39379 22.7476 6.85118C22.7476 7.30856 22.4292 7.67934 22.0365 7.67934H5.22868C4.83595 7.67934 4.51758 7.30856 4.51758 6.85118Z"
                        fill="#96A8BA"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4.51758 12.8743C4.51758 12.4169 4.83595 12.0461 5.22868 12.0461H22.0365C22.4292 12.0461 22.7476 12.4169 22.7476 12.8743C22.7476 13.3317 22.4292 13.7025 22.0365 13.7025H5.22868C4.83595 13.7025 4.51758 13.3317 4.51758 12.8743Z"
                        fill="#96A8BA"
                      />
                    </svg>
                  </span>{" "}
                  Manual
                </button>
                <button
                  className={!customeTime ? "active" : ""}
                  onClick={handleTimerClick}
                >
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="23"
                      viewBox="0 0 21 23"
                      fill="#96A8BA"
                    >
                      <path d="M7.88008 1.59379e-05C7.50837 0.00134006 7.20755 0.294417 7.20621 0.656569C7.20553 0.83136 7.27598 0.999304 7.40237 1.12333C7.52877 1.24736 7.70069 1.31754 7.88009 1.3182H9.82242V2.58363C4.34897 2.92702 0 7.35611 0 12.7718C0 18.4111 4.71168 23 10.502 23C16.2924 23 21 18.4111 21 12.7718C21 7.35419 16.6525 2.92487 11.1776 2.58363V1.3182H13.1199C13.3002 1.31886 13.4733 1.24934 13.6008 1.12509C13.7283 1.00085 13.7997 0.832236 13.799 0.656564C13.7983 0.481773 13.7263 0.31427 13.599 0.191123C13.4717 0.0679763 13.2993 -0.000656286 13.1199 4.73086e-06L7.88008 1.59379e-05ZM10.5015 3.86855C15.5609 3.86855 19.6466 7.84544 19.6466 12.7719C19.6466 17.6984 15.561 21.6821 10.5015 21.6821C5.44196 21.6821 1.35289 17.6988 1.35289 12.7719C1.35289 7.84544 5.44231 3.86855 10.5015 3.86855ZM10.5015 6.37386C6.88114 6.37386 3.93041 9.24594 3.93041 12.7722C3.93041 16.2984 6.88097 19.1716 10.5015 19.1716C14.122 19.1716 17.0697 16.2982 17.0697 12.7722C17.0697 9.24594 14.1216 6.37386 10.5015 6.37386ZM10.5015 7.6934C13.3909 7.6934 15.7153 9.95882 15.7153 12.7722C15.7153 15.5856 13.3909 17.8521 10.5015 17.8521C7.61212 17.8521 5.2836 15.5856 5.2836 12.7722C5.2836 9.95882 7.61212 7.6934 10.5015 7.6934ZM10.5015 9.23052C10.3221 9.22985 10.1497 9.29849 10.0224 9.42163C9.89491 9.54478 9.8231 9.71228 9.82242 9.88707V13.4106C9.82174 13.5863 9.8931 13.7549 10.0206 13.8792C10.1479 14.0034 10.3212 14.0729 10.5015 14.0723H14.118C14.2983 14.0729 14.4713 14.0034 14.5989 13.8792C14.7264 13.7549 14.7977 13.5863 14.7971 13.4106C14.7968 13.2356 14.725 13.0679 14.5975 12.9443C14.4702 12.8209 14.2976 12.7521 14.118 12.7528H11.1767V9.88705C11.1751 9.52445 10.8737 9.23119 10.5015 9.23052Z" />
                    </svg>
                  </span>{" "}
                  Timer
                </button>
              </div>
              {user && isUserAdmin && (isUserProfilesLoading || userProfiles.length > 0) && (
                <div className="bulk-import-time-tracker">
                  {isUserProfilesLoading ? (
                    <button
                      type="button"
                      className="btn-forpop-initem bulk-import"
                      style={{ pointerEvents: "none", cursor: "default" }}
                    >
                      <span className="icon-invoice">
                        <DownloadIcon />
                      </span>
                      Bulk Import
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-forpop-initem bulk-import"
                      onClick={handleBulkImportOpenModal}
                    >
                      <span className="icon-invoice">
                        <DownloadIcon />
                      </span>
                      Bulk Import
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Timer user filter */}
            {user && isUserAdmin && (isUserProfilesLoading || userProfiles.length > 0) && (
              <div className="tracker-filter-user-bulk-import-sec">
                <div className="tracker-filter-user users">
                  <label htmlFor="user">Tracking time for:</label>
                  <div className="custom-dropdown1" ref={userDropdownRef}>
                    <div className="drop-down-inner">
                      {isUserProfilesLoading ? (
                        <div
                          className="selected-options1"
                          style={{ pointerEvents: "none", cursor: "default" }}
                        >
                          <div className="selected-option1">
                            {myDisplayName ? `${myDisplayName} (me)` : "(me)"}
                          </div>
                        </div>
                      ) : (
                        <div
                          className="selected-options1"
                          onClick={toggleUserDropdown}
                        >
                          {selectedUser && selectedUserProfile ? (
                            <div className="selected-option1">
                              {selectedUserProfile.first_name || selectedUserProfile.company_first_name}{" "}
                              {selectedUserProfile.last_name || selectedUserProfile.company_last_name}{" "}
                              {selectedUserProfile._id === user?._id && "(me)"}
                            </div>
                          ) : (
                            <div className="placeholder">Select</div>
                          )}
                        </div>
                      )}
                      {isUserDropdownOpen && !isUserProfilesLoading && (
                        <div className="dropdown-list1">
                          {isUserAdmin && (
                            <>
                              <StatusDropdown
                                label="Filter by Status"
                                statusFilter={userStatusFilter}
                                setStatusFilter={(status) => {
                                  handleUserStatusChange(status);
                                  setUserSearchTerm("");
                                }}
                                isDropdownOpen={isUserStatusDropdownOpen}
                                setIsDropdownOpen={(open) => {
                                  setIsUserStatusDropdownOpen(open);
                                  if (open) setUserSearchTerm("");
                                }}
                                dropdownRef={userStatusDropdownRef}
                              />
                            </>
                          )}

                          <div className="top-search-tracker-for">
                            <input
                              type="text"
                              placeholder="Search teams"
                              value={userSearchTerm}
                              onChange={handleUserSearchChange}
                              className="search-bar"
                            />
                          </div>
                          <div className="tracker-who-list">
                            {filteredUsers
                              .filter((user) => {
                                const isAllowedUserCategory = user.user_category !== 3 && user.user_category !== 4;
                                const matchesStatusFilter = userStatusFilter === "All"
                                  ? true
                                  : userStatusFilter === "Active"
                                    ? user.is_active === true
                                    : user.is_active === false;
                                return isAllowedUserCategory && matchesStatusFilter;
                              })
                              //  .filter((user) => user.first_name && user.last_name) // Filter profiles with both first and last names
                              .sort((a, b) => {
                                const firstNameComparison = (
                                  a.first_name || a.company_first_name
                                ).localeCompare(
                                  b.first_name || b.company_first_name
                                );
                                if (firstNameComparison !== 0) {
                                  return firstNameComparison;
                                }
                                return (a.last_name || a.company_last_name || "").localeCompare(
                                  b.last_name || b.company_last_name || ""
                                );
                              })
                              .map((userF) => (
                                <div
                                  className="dropdown-item"
                                  key={userF._id}
                                  onClick={() => handleUserSelect(userF._id)}
                                >
                                  {`${userF.first_name || userF.company_first_name
                                    } ${userF.last_name || userF.company_last_name
                                    }`}{" "}
                                  {userF._id === user?._id && "(me)"}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <BulkImportModal
                  isOpen={isBulkImportModalOpen}
                  onClose={handleBulkImportCloseModal}
                  isLoading={isBulkImportLoading}
                  fileName={bulkImportFileName}
                  onFileChange={handleBulkImportFileChange}
                  onUpload={handleUpload}
                />

                <SkippedRowsModal
                  isOpen={showSkippedRowsModal}
                  onClose={() => {
                    setShowSkippedRowsModal(false);
                    handleBulkImportCloseModal();
                    if (trackedEntriesCount > 0) {
                      props.viewIsImport();
                    }
                  }}
                  skippedRows={skippedRowsList}
                  trackedEntriesCount={trackedEntriesCount}
                  onContinue={() => {
                    setShowSkippedRowsModal(false);
                    handleBulkImportCloseModal();
                    if (trackedEntriesCount > 0) {
                      props.viewIsImport();
                    }
                  }}
                />
              </div>
            )}
            {/* Timer User Filter end */}
          </div>
          {(isLoading || isGroupedTimerDataLoading) && (
            <LoadingModal
              isOpen={loadingModalIsOpen}
              onClose={() => setLoadingModalIsOpen(false)}
            />
          )}
          {/* timer */}
          {customeTime === false &&
            (appSettings.timeFormat === 12 ? (
              <div
                ref={timerBarRef}
                className="timetracker-overview-outer"
                style={getErrorBorderStyle(taskError)}
              >
                <div className="timetracker-inner">
                  <div className="timer-left ">
                    <TaskInput
                      taskName={taskName}
                      setTaskName={setTaskName}
                      setTaskError={setTaskError}
                      filteredData={filteredData}
                      setFilteredData={setFilteredData}
                      groupedTimerData={groupedTimerData}
                      dropdownVisible={dropdownVisible}
                      setDropdownVisible={setDropdownVisible}
                      handleInputClick={handleInputClick}
                      autoLoad={autoLoad}
                      autoFillDropdownRef={autoFillDropdownRef}
                    />
                  </div>
                  <div className="timer-right">
                    <ProjectSelector
                      projectDropdownRef={projectDropdownRef}
                      projectDropdownOpen={projectDropdownOpen}
                      setProjectDropdownOpen={setProjectDropdownOpen}
                      projectStatusFilter={projectStatusFilter}
                      setProjectStatusFilter={setProjectStatusFilter}
                      isProjectStatusDropdownOpen={isProjectStatusDropdownOpen}
                      setIsProjectStatusDropdownOpen={setIsProjectStatusDropdownOpen}
                      projectStatusDropdownRef={projectStatusDropdownRef}
                      filter={filter}
                      setFilter={setFilter}
                      selectedClients={selectedClients}
                      setSelectedClients={setSelectedClients}
                      selectedProject={selectedProject}
                      setSelectedProject={setSelectedProject}
                      filteredClientData={filteredClientData}
                      user={user}
                      letterCount={letterCount}
                      trackerOuterRef2={trackerOuterRef2}
                      projectName={projectName}
                      sprintName={sprintName}
                      client={client}
                      setProjectName={setProjectName}
                      setProjectId={setProjectId}
                      setClient={setClient}
                      setClientId={setClientId}
                      setIsBillable={setIsBillable}
                      setSprintId={setSprintId}
                      setSprintName={setSprintName}
                      setTaskError={setTaskError}
                      setTagDropdownOpen={setTagDropdownOpen}
                    />
                    <div className="tag-billing">
                      <TagDropdown
                        tag={tag}
                        tagId={tagId}
                        setTag={setTag}
                        setTagId={setTagId}
                        tagDropdownOpen={tagDropdownOpen}
                        setTagDropdownOpen={setTagDropdownOpen}
                        setProjectDropdownOpen={setProjectDropdownOpen}
                        handleTagInputChange={handleTagInputChange}
                        filteredTagItems={filteredTagItems}
                        tagDropdownRef={tagDropdownRef}
                      />
                      <BillableIcon billable={isBillable} />
                    </div>

                    <LiveTimer
                      start={start}
                      isRunning={isRunning}
                      customeTime={customeTime}
                      isOnline={isOnline}
                      stopLoading={stopLoading}
                      user={user}
                      selectedUser={selectedUser}
                      trigger={trigger}
                      handleEndClick={handleEndClick}
                      handleStartClick={handleStartClick}
                      dateSection={timerDateSection}
                      calendarPortalRef={timerCalendarPortalRef}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div
                ref={timerBarRef}
                className="timetracker-overview-outer"
                style={getErrorBorderStyle(taskError)}
              >
                <div className="timetracker-inner">
                  <div className="timer-left ">
                    <TaskInput
                      taskName={taskName}
                      setTaskName={setTaskName}
                      setTaskError={setTaskError}
                      filteredData={filteredData}
                      setFilteredData={setFilteredData}
                      groupedTimerData={groupedTimerData}
                      dropdownVisible={dropdownVisible}
                      setDropdownVisible={setDropdownVisible}
                      handleInputClick={handleInputClick}
                      autoLoad={autoLoad}
                      autoFillDropdownRef={autoFillDropdownRef}
                    />
                  </div>
                  <div className="timer-right">
                    <div className="project-sprint" ref={projectDropdownRef}>
                      <Col
                        style={pointerCursorStyle}
                        onClick={handleProjectDropdownToggle}
                        className="project-sprint-outer"
                      >
                        {renderProjectNameDisplay()}
                      </Col>

                      {projectDropdownOpen && (
                        <div className="custom-dropdown new-active-inactive">
                          {renderProjectDropdownWithFilter(
                            !isUserAdmin ? 'filter-active-no' : '',
                            true
                          )}
                        </div>
                      )}
                    </div>
                    <div className="tag-billing">
                      <TagDropdown
                        tag={tag}
                        tagId={tagId}
                        setTag={setTag}
                        setTagId={setTagId}
                        tagDropdownOpen={tagDropdownOpen}
                        setTagDropdownOpen={setTagDropdownOpen}
                        setProjectDropdownOpen={setProjectDropdownOpen}
                        handleTagInputChange={handleTagInputChange}
                        filteredTagItems={filteredTagItems}
                        tagDropdownRef={tagDropdownRef}
                      />
                      <BillableIcon billable={isBillable} />
                    </div>

                    <LiveTimer
                      start={start}
                      isRunning={isRunning}
                      customeTime={customeTime}
                      isOnline={isOnline}
                      stopLoading={stopLoading}
                      user={user}
                      selectedUser={selectedUser}
                      trigger={trigger}
                      handleEndClick={handleEndClick}
                      handleStartClick={handleStartClick}
                      dateSection={timerDateSection}
                      calendarPortalRef={timerCalendarPortalRef}
                    />
                  </div>
                </div>
              </div>
            ))
          }
          {/* timer end */}
          {customeTime === true && (
            <div
              ref={timerBarRef}
              className="timetracker-overview-outer timetracker-outer-custom"
              style={getErrorBorderStyle(taskError)}
            >
              <div className="timetracker-inner small-device-desktop">
                <div className="timer-left">
                  <div className="project-name">
                    <Col className="cursor" ref={autoFillDropdownRef}>
                      <input
                        type="text"
                        value={taskName ? taskName : ""}
                        onClick={() => setDropdownVisible(true)}
                        onFocus={() => setDropdownVisible(true)}
                        onChange={handleManualTaskInputChange}
                        placeholder="You’re working on..."
                        className={taskError ? taskInputErrorClassName : taskInputClassName}
                        style={textOverflowStyle}
                        autoComplete="off"
                      />
                      {filteredData.length > 0 && dropdownVisible && (
                        <div className="dropdown">
                          <ul>
                            {filteredData.slice(0, 8).map((item) => (
                              <li
                                key={item._id}
                                onClick={() => {
                                  autoLoad(
                                    item.task,
                                    item.project?._id,
                                    item.project?.project_name,
                                    item.tag ? item.tag.tag_name : null,
                                    item.tag ? item.tag._id : null,
                                    item.client,
                                    item.project?.client_name,
                                    item.sprint ? item.sprint.sprint_name : null,
                                    item.sprint ? item.sprint._id : null,
                                    item._id,
                                    item.billable
                                  );
                                }}
                              >
                                <div className="project-des">{item.task}</div>
                                <div className="project-sug-name">
                                  {item.project?.project_name} {`${item.sprint ? ` - ${item.sprint.sprint_name}` : ""}`}
                                  <span className="project-tag-sug">
                                    {item.client}
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Col>
                  </div>

                  {windowWidth >= 200 && windowWidth <= 1440 && (
                    <div className="project-sprint hide-on-large-desktop" ref={projectDropdownRef}>
                      <Col
                        style={pointerCursorStyle}
                        onClick={handleProjectDropdownToggle}
                        className="project-sprint-outer"
                      >
                        {renderProjectNameDisplaySmall("150px")}
                      </Col>
                      {projectDropdownOpen && (
                        <div className="custom-dropdown new-active-inactive">
                          {renderProjectDropdownWithFilter(
                            !isUserAdmin ? "filter-active-no" : "",
                            true
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="timer-right">
                  {windowWidth >= 1441 && (
                    <div
                      className="project-sprint hide-on-small-desktop"
                      ref={projectDropdownRef}
                    >
                      <Col
                        style={pointerCursorStyle}
                        onClick={handleProjectDropdownToggle}
                        className="project-sprint-outer"
                      >
                        {renderProjectNameDisplaySmall("150px")}
                      </Col>
                      {projectDropdownOpen && (
                        <div className="custom-dropdown new-active-inactive">
                          {renderProjectDropdownWithFilter(
                            !isUserAdmin ? 'filter-active-no' : '',
                            true
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="outer-small-device-timer">
                    <div className="inner-left-samll-device">
                      {windowWidth >= 1200 && windowWidth <= 1440 && renderInlineTagDropdown("150px")}
                    </div>
                    <div className="inner-right-small-device">
                      <div className="tag-billing">
                        {(windowWidth >= 1441 || windowWidth < 1200) && renderInlineTagDropdown("150px")}
                        {renderBillableIcon()}
                      </div>
                      <div className="manual-time-sec manual-time-sec-with-time-icon">
                        <div className="time-overview-sec">
                          {appSettings.timeFormat === 24 ? (
                            <>
                              <TimeInputWithPicker
                                pickerType="start24"
                                value={startTimeManual24}
                                setValue={setStartTimeManual24}
                                inputRef={startInputRef}
                                timePickerRefs={timePickerRefs}
                                allInputRefs={{
                                  startInputRef,
                                  endInputRef,
                                  start12HrTimeInputRef,
                                  end12HrTimeInputRef,
                                }}
                                timePickerOpen={timePickerOpen}
                                setTimePickerOpen={setTimePickerOpen}
                                timePickerSelection={timePickerSelection}
                                setTimePickerSelection={setTimePickerSelection}
                                timePickerFilter={timePickerFilter}
                                setTimePickerFilter={setTimePickerFilter}
                                isSettingFromDropdownRef={isSettingFromDropdownRef}
                                justOpenedDropdownRef={justOpenedDropdownRef}
                                setSplitted={setSplitted}
                                setSplittedDate={setSplittedDate}
                                setIsTimeInputActive={setIsTimeInputActive}
                                manualChangeStartTime={manualChangeStartTime}
                                manualChangeStartTime24={manualChangeStartTime24}
                                manualChangeEndTime={manualChangeEndTime}
                                manualChangeEndTime24={manualChangeEndTime24}
                                error={uManualStartTimeError}
                                is24Hour={true}
                              />
                              -
                              <TimeInputWithPicker
                                pickerType="end24"
                                value={endTimeManual24}
                                setValue={setEndTimeManual24}
                                inputRef={endInputRef}
                                timePickerRefs={timePickerRefs}
                                allInputRefs={{
                                  startInputRef,
                                  endInputRef,
                                  start12HrTimeInputRef,
                                  end12HrTimeInputRef,
                                }}
                                timePickerOpen={timePickerOpen}
                                setTimePickerOpen={setTimePickerOpen}
                                timePickerSelection={timePickerSelection}
                                setTimePickerSelection={setTimePickerSelection}
                                timePickerFilter={timePickerFilter}
                                setTimePickerFilter={setTimePickerFilter}
                                isSettingFromDropdownRef={isSettingFromDropdownRef}
                                justOpenedDropdownRef={justOpenedDropdownRef}
                                setSplitted={setSplitted}
                                setSplittedDate={setSplittedDate}
                                setIsTimeInputActive={setIsTimeInputActive}
                                manualChangeStartTime={manualChangeStartTime}
                                manualChangeStartTime24={manualChangeStartTime24}
                                manualChangeEndTime={manualChangeEndTime}
                                manualChangeEndTime24={manualChangeEndTime24}
                                error={uManualEndTimeError}
                                is24Hour={true}
                              />
                            </>
                          ) : (
                            <>
                              <TimeInputWithPicker
                                pickerType="start12"
                                value={startTimeManual}
                                setValue={setStartTimeManual}
                                inputRef={start12HrTimeInputRef}
                                timePickerRefs={timePickerRefs}
                                allInputRefs={{
                                  startInputRef,
                                  endInputRef,
                                  start12HrTimeInputRef,
                                  end12HrTimeInputRef,
                                }}
                                timePickerOpen={timePickerOpen}
                                setTimePickerOpen={setTimePickerOpen}
                                timePickerSelection={timePickerSelection}
                                setTimePickerSelection={setTimePickerSelection}
                                timePickerFilter={timePickerFilter}
                                setTimePickerFilter={setTimePickerFilter}
                                isSettingFromDropdownRef={isSettingFromDropdownRef}
                                justOpenedDropdownRef={justOpenedDropdownRef}
                                setSplitted={setSplitted}
                                setSplittedDate={setSplittedDate}
                                setIsTimeInputActive={setIsTimeInputActive}
                                manualChangeStartTime={manualChangeStartTime}
                                manualChangeStartTime24={manualChangeStartTime24}
                                manualChangeEndTime={manualChangeEndTime}
                                manualChangeEndTime24={manualChangeEndTime24}
                                error={uManualStartTimeError}
                                splitted={splitted}
                                is24Hour={false}
                              />
                              -
                              <TimeInputWithPicker
                                pickerType="end12"
                                value={endTimeManual}
                                setValue={setEndTimeManual}
                                inputRef={end12HrTimeInputRef}
                                timePickerRefs={timePickerRefs}
                                allInputRefs={{
                                  startInputRef,
                                  endInputRef,
                                  start12HrTimeInputRef,
                                  end12HrTimeInputRef,
                                }}
                                timePickerOpen={timePickerOpen}
                                setTimePickerOpen={setTimePickerOpen}
                                timePickerSelection={timePickerSelection}
                                setTimePickerSelection={setTimePickerSelection}
                                timePickerFilter={timePickerFilter}
                                setTimePickerFilter={setTimePickerFilter}
                                isSettingFromDropdownRef={isSettingFromDropdownRef}
                                justOpenedDropdownRef={justOpenedDropdownRef}
                                setSplitted={setSplitted}
                                setSplittedDate={setSplittedDate}
                                setIsTimeInputActive={setIsTimeInputActive}
                                manualChangeStartTime={manualChangeStartTime}
                                manualChangeStartTime24={manualChangeStartTime24}
                                manualChangeEndTime={manualChangeEndTime}
                                manualChangeEndTime24={manualChangeEndTime24}
                                error={uManualEndTimeError}
                                splitted={splitted}
                                is24Hour={false}
                              />
                            </>
                          )}
                          <div className="time--sec-icon">
                            <ClockIcon />
                          </div>
                        </div>
                        <div className="date-overview-sec manual-tracker-outer-sec" ref={manualDateContainerRef}>
                          <div
                            onClick={handleDateIconClick}
                            className="custom-date-icon"
                          >
                            <DatePickerIconSVG />
                          </div>
                          <DatePicker
                            ref={datePickerRef}
                            value={datePickerValue}
                            onChange={(value) => {
                              try {
                                const convertedDate = moment.tz(value, global.config.getTimeZone()).toDate();
                                setDate(convertedDate);
                              } catch (error) {
                                console.error("Error in DatePicker onChange:", error);
                              }
                            }}
                            format="dd/MM/yyyy"
                            className="timer-date time-date-manual"
                            menuClassName="timer-outer-datepicker-menu"
                            container={() =>
                              manualDateContainerRef.current?.closest(".timetracker-overview-outer") ||
                              manualDateContainerRef.current?.closest(".manual-time-sec") ||
                              manualDateContainerRef.current ||
                              document.body
                            }
                            placement="bottomEnd"
                            oneTap
                            isoWeek={appSettings.weekStartDay === "Monday"}
                            shouldDisableDate={shouldDisableDate}
                            onEnter={() => setIsTimerDatePickerOpen(true)}
                            onExit={() => setIsTimerDatePickerOpen(false)}
                            onOpen={() => setIsTimerDatePickerOpen(true)}
                            onClose={() => setIsTimerDatePickerOpen(false)}
                          />
                          <div className="day-plus">{splitted ? "+1" : ""}</div>
                        </div>
                      </div>

                      <div className="start-timer-outer">
                        <Col className="start-timer-inner">
                          {appSettings.timeFormat === 24 ? (
                            <DurationInput
                              value={uFormatedTimeManual}
                              onChange={(e) => manualTimeShowEdit24(e.target.value)}
                              error={uManualFormatedTimeError}
                            />
                          ) : (
                            <DurationInput
                              value={uFormatedTimeManual}
                              onChange={(e) => manualTimeShowEdit(e.target.value)}
                              error={uManualFormatedTimeError}
                            />
                          )}

                          {isRunning === true && customeTime === false && (
                            <button
                              onClick={handleEndClick}
                              className="button-pad bg-pink-500 text-white active:bg-pink-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                              type="button"
                            >
                              Stop
                            </button>
                          )}

                          {customeTime === true && (
                            <>
                              {isTimeInputActive ? (
                                <div className="button-pad-add" style={disabledButtonStyle}>
                                  <PlusCircleIcon />
                                </div>
                              ) : (
                                <button
                                  onClick={handleSetCustomeTimes}
                                  className="button-pad-add"
                                  disabled={
                                    ((isCustomeTimeLoading ||
                                      uManualFormatedTimeError ||
                                      uManualEndTimeError ||
                                      uManualStartTimeError) && !splitted) &&
                                    "disabled"
                                  }
                                  type="button"
                                >
                                  {isCustomeTimeLoading ? (
                                    <div className="add-btn-spinner" />
                                  ) : (
                                    <PlusCircleIcon />
                                  )}
                                </button>
                              )}
                            </>
                          )}
                        </Col>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* <ToastContainer /> */}

        <TimerList
          currentPageData={currentPageData}
          timer={timer}
          isTimerDataLoading={isTimerLoading}
          selectedUser={selectedUser}
          leastDate={leastDate}
          highestDate={highestDate}
          leastLastWeekDate={leastLastWeekDate}
          highestLastWeekDate={highestLastWeekDate}
          thisWeekRendered={thisWeekRendered.current}
          WeekTotalDisplay={WeekTotalDisplay}
          lastWeekRendered={lastWeekRendered.current}
          getMomentDate={getMomentDate}
          extremesByWeek={extremesByWeek}
          findLeastDate={findLeastDate}
          findExtremeDate={findExtremeDate}
          TimerCard={TimerCard}
          resumeTimeTracking={resumeTimeTracking}
          appSettings={appSettings}
          resumeLoading={resumeLoading}
          customeTime={customeTime}
          Modal={Modal}
          taskModalIsOpen={taskModalIsOpen}
          setTaskModalIsOpen={setTaskModalIsOpen}
          taskName={taskName}
          setTaskName={setTaskName}
          showingDropdownRef={showingDropdownRef}
          setShowingDropdownOpen={setShowingDropdownOpen}
          showingDropdownOpen={showingDropdownOpen}
          totalResults={totalResults}
          perPage={perPage}
          setPerPage={setPerPage}
          setSelectedPage={setSelectedPage}
          pageCount={pageCount}
          ReactPaginate={ReactPaginate}
          selectedPage={selectedPage}
          handlePageChange={handlePageChange}
          globalEditedId={editedId}
          setGlobalEditedId={setEditedId}
          refetchTimerData={refetchTimerData}
        />
      </div>
      {showTimeZoneModal === true && <TimeZoneModal />}
      {/* Pulse animation style */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
      `}</style>
    </motion.div>
  );
};

export default Tracker;